import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge, CHECK_TYPE_LABEL } from "@/components/StatusBadge";
import CaseAttachments from "@/components/CaseAttachments";
import { FilePlus2, MapPin, Scale, FileCheck, FolderOpen } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { address: MapPin, litigation: Scale, id: FileCheck };

export default function ClientDashboard() {
  const [stats, setStats] = useState(null);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCase, setOpenCase] = useState(null);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/checks/stats"), api.get("/checks")]);
      setStats(s.data); setChecks(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">My verification checks</h1>
          <p className="text-slate-600 mt-1 text-sm">Track case status and upload candidate consent forms.</p>
        </div>
        <Link to="/dashboard/new-check">
          <Button data-testid="dashboard-new-check-btn" className="pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-11">
            <FilePlus2 size={16} className="mr-2" /> New check
          </Button>
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "Total", v: stats?.total ?? 0, cls: "text-slate-900" },
          { k: "Pending", v: stats?.by_status.Pending ?? 0, cls: "text-amber-700" },
          { k: "In Progress", v: stats?.by_status["In Progress"] ?? 0, cls: "text-blue-700" },
          { k: "Completed", v: stats?.by_status.Completed ?? 0, cls: "text-emerald-700" },
        ].map((s) => (
          <div key={s.k} className="border border-slate-200 rounded-2xl bg-white p-5" data-testid={`stat-${s.k.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">{s.k}</div>
            <div className={`mt-2 font-display text-3xl font-bold ${s.cls}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-slate-200 rounded-2xl bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold text-slate-900">Recent checks</div>
          <div className="text-xs text-slate-500">{checks.length} total</div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : checks.length === 0 ? (
          <div className="p-14 text-center">
            <p className="text-slate-600">You haven&apos;t submitted any checks yet.</p>
            <Link to="/dashboard/new-check" className="inline-block mt-4">
              <Button className="pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">Submit your first check</Button>
            </Link>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ref #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Candidate</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Promised by</TableHead>
                <TableHead>Files</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {checks.map((c) => {
                const Icon = TYPE_ICON[c.check_type] || FileCheck;
                return (
                  <TableRow key={c.id} data-testid={`check-row-${c.reference_number}`}>
                    <TableCell className="font-mono-data text-xs">{c.reference_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <Icon size={14} className="text-[#1E3A8A]" /> {CHECK_TYPE_LABEL[c.check_type]}
                        {c.litigation_type ? <span className="ml-1 text-xs text-slate-500">({c.litigation_type})</span> : null}
                      </div>
                    </TableCell>
                    <TableCell>{c.candidate_name}</TableCell>
                    <TableCell>{c.country}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className="text-xs text-slate-600">{c.promised_by ? c.promised_by.slice(0, 10) : <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-xs font-mono-data">{c.attachments_count || 0}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setOpenCase(c)}
                        data-testid={`manage-${c.reference_number}`}
                        className="rounded-full">
                        <FolderOpen size={14} className="mr-1.5" /> Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!openCase} onOpenChange={(o) => !o && setOpenCase(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Case {openCase?.reference_number}</DialogTitle>
          </DialogHeader>
          {openCase && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3 text-slate-700">
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Candidate</div>{openCase.candidate_name}</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Country</div>{openCase.country}</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Status</div><StatusBadge status={openCase.status} /></div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Promised by</div>{openCase.promised_by ? openCase.promised_by.slice(0, 10) : "—"}</div>
              </div>
              {openCase.remarks ? (
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500 mb-1">Remarks from admin</div>
                  <div className="text-slate-700 rounded-lg bg-slate-50 p-3 border border-slate-200">{openCase.remarks}</div>
                </div>
              ) : null}
              <CaseAttachments
                caseId={openCase.id}
                attachments={openCase.attachments || []}
                canUpload={true}
                onChanged={async () => { await load(); const fresh = (await api.get("/checks")).data.find((x) => x.id === openCase.id); if (fresh) setOpenCase(fresh); }}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCase(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
